import React, { useEffect, useState } from 'react';
import {
  FormField,
  TextField,
  TextArea,
  Callout,
  Table,
  Text,
  Icon,
  Button,
  Select,
  Flex,
} from '@scale/scaleui-radix';
import { CompassTransformScript } from '@scale/genai-compass-shared/cards/transform_script';
import type { Column } from '@scale/compass-shared/types';
import {
  OTSUIPromptGenerationParams,
  CDSDownloadResponseParams,
  CDSContentInsertionParams,
  CDSUrlSigningParams,
  S3UrlSigningParams,
  HubstaffScreenshotParams,
  MultiMangoQATableParams,
  MultimangoPasswordResetParams,
  SetCBRoleParams,
  SyncMultimangoUsersParams,
  WfeDecryptPasswordsParams,
  AgentExecutionParams,
  getDefaultScriptParams,
  Distribution,
  DEFAULT_CDS_CONTENT_INSERTION_COLUMN_MAPPING,
  DEFAULT_CDS_URL_SIGNING_COLUMN_MAPPING,
  DEFAULT_S3_URL_SIGNING_COLUMN_MAPPING,
} from '../models/ScriptParams';
import {
  faInfoCircle,
  faPlus,
  faTrash,
  faExclamationTriangle,
  faEye,
  faEyeSlash,
} from '@fortawesome/pro-solid-svg-icons';
interface ScriptParamsComponentProps {
  script?: CompassTransformScript;
  params: Record<string, any>;
  onChange: (params: Record<string, any>) => void;
  columns?: Column[];
}

const DistributionEditor: React.FC<{
  label: string;
  distribution: Distribution;
  onChange: (newDistribution: Distribution) => void;
}> = ({ label, distribution, onChange }) => {
  const [localDistribution, setLocalDistribution] = useState<Distribution>(distribution);

  useEffect(() => {
    if (distribution) {
      setLocalDistribution(distribution);
    }
  }, [distribution]);

  const handleValueChange = (key: string, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      const newDistribution = { ...localDistribution, [key]: numValue };
      setLocalDistribution(newDistribution);
      onChange(newDistribution);
    }
  };

  return (
    <FormField.Root className="mb-4 flex-grow min-w-[300px] max-w-[450px]">
      <FormField.Label>{label}</FormField.Label>
      <div className="mb-2">
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Option</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Percentage</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {Object.entries(localDistribution).map(([key, value]) => (
              <Table.Row key={key}>
                <Table.Cell>{key}</Table.Cell>
                <Table.Cell>
                  <TextField.Root
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={value.toString()}
                    onChange={e => handleValueChange(key, e.target.value)}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </div>
    </FormField.Root>
  );
};

// We need to define a generic component for this and then pass in the params and onChange function
// If more scripts want to define exact param types then we can make interfaces for that but for now this should be fine

// i..e interface ParameterMetadata {
//     type: 'text' | 'number' | 'distribution' | 'select' | 'array';
//     label: string;
//     description?: string;
//     options?: string[]; // For select type
//     defaultValue: any;
//   }

// then something like

// const SCRIPT_PARAMETERS_REGISTRY: Record<CompassTransformScript, ScriptParametersMetadata> = {
// [CompassTransformScript.OTS_UI_PROMPT_GENERATION]: {
//     languageDistribution: {
//       type: 'distribution',
//       label: 'Language Distribution',
//       description: 'Distribution of programming languages to use',
//       defaultValue: { ... }
//     },
//     layoutDistribution: {
//       type: 'distribution',
//       label: 'Layout Distribution',
//       defaultValue: { ... }
//     },
//     // ...etc
//   },
// };

const OTSUIPromptGenerationParamsComponent: React.FC<{
  params: OTSUIPromptGenerationParams;
  onChange: (params: OTSUIPromptGenerationParams) => void;
}> = ({ params, onChange }) => {
  // Ensure we have default values if params are empty
  const defaultParams = getDefaultScriptParams(
    CompassTransformScript.OTS_UI_PROMPT_GENERATION,
  ) as OTSUIPromptGenerationParams;

  // Use default values if properties are undefined
  const languageDistribution = params.languageDistribution || defaultParams.languageDistribution;
  const layoutDistribution = params.layoutDistribution || defaultParams.layoutDistribution;
  const categoryDistribution = params.categoryDistribution || defaultParams.categoryDistribution;
  const similarityThreshold = params.similarityThreshold || defaultParams.similarityThreshold;

  const handleLanguageDistributionChange = (newDistribution: Distribution) => {
    onChange({ ...params, languageDistribution: newDistribution });
  };

  const handleLayoutDistributionChange = (newDistribution: Distribution) => {
    onChange({ ...params, layoutDistribution: newDistribution });
  };

  const handleCategoryDistributionChange = (newDistribution: Distribution) => {
    onChange({ ...params, categoryDistribution: newDistribution });
  };

  const handleSimilarityThresholdChange = (newThreshold: Distribution) => {
    onChange({ ...params, similarityThreshold: newThreshold });
  };

  return (
    <div className="flex flex-col gap-4">
      <Callout.Root color="neutral" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          Configure parameters for OTS UI Prompt Generation script. These parameters will be used to
          generate UI prompts.
        </Callout.Text>
      </Callout.Root>

      <div className="flex flex-wrap gap-4">
        <DistributionEditor
          label="Language Distribution"
          distribution={languageDistribution}
          onChange={handleLanguageDistributionChange}
        />
        <DistributionEditor
          label="Layout Distribution"
          distribution={layoutDistribution}
          onChange={handleLayoutDistributionChange}
        />
        <DistributionEditor
          label="Category Distribution"
          distribution={categoryDistribution}
          onChange={handleCategoryDistributionChange}
        />
        <DistributionEditor
          label="Similarity Threshold"
          distribution={similarityThreshold}
          onChange={handleSimilarityThresholdChange}
        />
      </div>
    </div>
  );
};

const CDSDownloadResponseParamsComponent: React.FC<{
  params: CDSDownloadResponseParams;
  onChange: (params: CDSDownloadResponseParams) => void;
}> = ({ params, onChange }) => {
  // Ensure we have default values if params are empty
  const defaultParams = getDefaultScriptParams(
    CompassTransformScript.CDS_DOWNLOAD_RESPONSE,
  ) as CDSDownloadResponseParams;

  // Use default value if jsonata is undefined
  const jsonata = params.jsonata ?? defaultParams.jsonata;

  const handleJsonataChange = (newJsonata: string) => {
    onChange({ ...params, jsonata: newJsonata });
  };

  return (
    <div className="flex flex-col gap-4 max-w-4xl">
      <Callout.Root color="neutral" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          Configure JSONata expression for CDS Download Response transformation. The expression will
          be used to transform the downloaded data.
        </Callout.Text>
      </Callout.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>JSONata Expression</FormField.Label>
        <TextArea
          value={jsonata}
          onChange={e => handleJsonataChange(e.target.value)}
          placeholder="Enter JSONata expression (e.g., $)"
          autoResize
          className="min-h-[120px] min-w-[400px] font-mono text-sm"
          rows={6}
        />
        <FormField.Description>
          JSONata expression to transform the downloaded response data. Use "$" for the root object.
        </FormField.Description>
      </FormField.Root>
    </div>
  );
};

const CDSContentInsertionParamsComponent: React.FC<{
  params: CDSContentInsertionParams;
  onChange: (params: CDSContentInsertionParams) => void;
  columns?: Column[];
}> = ({ params, onChange, columns = [] }) => {
  const columnMapping = params.columnMapping ?? DEFAULT_CDS_CONTENT_INSERTION_COLUMN_MAPPING;

  // Automatically set the first column when columns become available and we're using the default empty mapping
  useEffect(() => {
    if (
      columns.length > 0 &&
      (!params.columnMapping ||
        (Object.keys(params.columnMapping).length === 1 &&
          Object.keys(params.columnMapping)[0] === '' &&
          params.columnMapping[''] === ''))
    ) {
      const newMapping = { [columns[0].id]: '' };
      onChange({ ...params, columnMapping: newMapping });
    }
  }, [columns, params, onChange]);

  const handleColumnMappingChange = (sourceColumn: string, destinationColumn: string) => {
    const newMapping = { ...columnMapping, [sourceColumn]: destinationColumn };
    onChange({ ...params, columnMapping: newMapping });
  };

  const handleAddMapping = () => {
    // Generate a unique placeholder source column name to avoid conflicts
    let counter = 1;
    let sourceColumnName = 'new_source_column';
    while (columnMapping[sourceColumnName]) {
      sourceColumnName = `new_source_column_${counter}`;
      counter++;
    }

    const newMapping = { ...columnMapping, [sourceColumnName]: 'new_destination_column' };
    onChange({ ...params, columnMapping: newMapping });
  };

  const handleRemoveMapping = (sourceColumn: string) => {
    const newMapping = { ...columnMapping };
    delete newMapping[sourceColumn];
    onChange({ ...params, columnMapping: newMapping });
  };

  const handleSourceColumnChange = (oldSourceColumn: string, newSourceColumn: string) => {
    if (oldSourceColumn === newSourceColumn) return;

    const newMapping = { ...columnMapping };
    const destinationColumn = newMapping[oldSourceColumn];
    delete newMapping[oldSourceColumn];
    newMapping[newSourceColumn] = destinationColumn;
    onChange({ ...params, columnMapping: newMapping });
  };

  return (
    <div className="flex flex-col gap-4 max-w-4xl">
      <Callout.Root color="neutral" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          Configure column mappings for CDS Content Insertion transformation. Specify which columns
          to process and what to name the output columns containing the fetched content.
        </Callout.Text>
      </Callout.Root>

      {columns.length === 0 && (
        <Callout.Root color="red" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Warning" />
          </Callout.Icon>
          <Callout.Text>
            No data columns available. You need to have data from a previous step to use this
            transformation.
          </Callout.Text>
        </Callout.Root>
      )}

      <FormField.Root className="mb-4">
        <FormField.Label>Column Mapping</FormField.Label>
        <div className="border rounded-lg p-4">
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Source Column (with CDS S3 URLs)</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Destination Column (content)</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="100px">Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {Object.entries(columnMapping).map(([sourceColumn, destinationColumn]) => (
                <Table.Row key={sourceColumn}>
                  <Table.Cell>
                    {columns.length > 0 ? (
                      <Select.Root
                        value={sourceColumn}
                        onValueChange={newSourceColumn =>
                          handleSourceColumnChange(sourceColumn, newSourceColumn)
                        }
                      >
                        <Select.Trigger className="w-full" placeholder="Select source column..." />
                        <Select.Content>
                          {columns.map(column => (
                            <Select.Item key={column.id} value={column.id}>
                              {column.header || column.id}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    ) : (
                      <TextField.Root
                        value={sourceColumn}
                        onChange={e => handleSourceColumnChange(sourceColumn, e.target.value)}
                        placeholder="e.g., source_url"
                        className="w-full"
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <TextField.Root
                      value={destinationColumn}
                      onChange={e => handleColumnMappingChange(sourceColumn, e.target.value)}
                      placeholder="e.g., source_content"
                      className="w-full"
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      variant="soft"
                      color="red"
                      size="1"
                      onClick={() => handleRemoveMapping(sourceColumn)}
                    >
                      <Icon icon={faTrash} label="Remove" />
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          <div className="mt-4">
            <Button variant="soft" color="blue" size="1" onClick={handleAddMapping}>
              <Icon icon={faPlus} label="Add" />
              Add Column Mapping
            </Button>
          </div>
        </div>
        <FormField.Description>
          Map source columns containing CDS URLs to destination column names for the fetched
          content. The transformer will create new columns with the specified destination names.
        </FormField.Description>
      </FormField.Root>
    </div>
  );
};

const CDSUrlSigningParamsComponent: React.FC<{
  params: CDSUrlSigningParams;
  onChange: (params: CDSUrlSigningParams) => void;
  columns?: Column[];
}> = ({ params, onChange, columns = [] }) => {
  const columnMapping = params.columnMapping ?? DEFAULT_CDS_URL_SIGNING_COLUMN_MAPPING;
  const ttlSeconds = params.ttlSeconds ?? 7 * 24 * 60 * 60; // Default: 7 days

  // Automatically set the first column when columns become available and we're using the default empty mapping
  useEffect(() => {
    if (
      columns.length > 0 &&
      (!params.columnMapping ||
        (Object.keys(params.columnMapping).length === 1 &&
          Object.keys(params.columnMapping)[0] === '' &&
          params.columnMapping[''] === ''))
    ) {
      const newMapping = { [columns[0].id]: '' };
      onChange({ ...params, columnMapping: newMapping });
    }
  }, [columns, params, onChange]);

  const handleColumnMappingChange = (sourceColumn: string, destinationColumn: string) => {
    const newMapping = { ...columnMapping, [sourceColumn]: destinationColumn };
    onChange({ ...params, columnMapping: newMapping });
  };

  const handleAddMapping = () => {
    // Generate a unique placeholder source column name to avoid conflicts
    let counter = 1;
    let sourceColumnName = 'new_source_column';
    while (columnMapping[sourceColumnName]) {
      sourceColumnName = `new_source_column_${counter}`;
      counter++;
    }

    const newMapping = { ...columnMapping, [sourceColumnName]: 'new_destination_column' };
    onChange({ ...params, columnMapping: newMapping });
  };

  const handleRemoveMapping = (sourceColumn: string) => {
    const newMapping = { ...columnMapping };
    delete newMapping[sourceColumn];
    onChange({ ...params, columnMapping: newMapping });
  };

  const handleSourceColumnChange = (oldSourceColumn: string, newSourceColumn: string) => {
    if (oldSourceColumn === newSourceColumn) return;

    const newMapping = { ...columnMapping };
    const destinationColumn = newMapping[oldSourceColumn];
    delete newMapping[oldSourceColumn];
    newMapping[newSourceColumn] = destinationColumn;
    onChange({ ...params, columnMapping: newMapping });
  };

  const handleTtlSecondsChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      onChange({ ...params, ttlSeconds: numValue });
    }
  };

  return (
    <div className="flex flex-col gap-4 max-w-4xl">
      <Callout.Root color="neutral" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          Configure column mappings for CDS URL Signing transformation. Specify which columns
          contain JSON with private CDS URLs and what to name the output columns with signed URLs.
        </Callout.Text>
      </Callout.Root>

      {columns.length === 0 && (
        <Callout.Root color="red" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Warning" />
          </Callout.Icon>
          <Callout.Text>
            No data columns available. You need to have data from a previous step to use this
            transformation.
          </Callout.Text>
        </Callout.Root>
      )}

      <FormField.Root className="mb-4">
        <FormField.Label>TTL for Signed URLs (seconds)</FormField.Label>
        <TextField.Root
          type="number"
          min="60"
          value={ttlSeconds.toString()}
          onChange={e => handleTtlSecondsChange(e.target.value)}
          placeholder="604800"
          className="max-w-xs"
        />
        <FormField.Description>
          Time-to-live for signed URLs in seconds. Default is 604800 seconds (7 days). Common
          values: 3600 (1 hour), 86400 (1 day), 604800 (7 days).
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>Column Mapping</FormField.Label>
        <div className="border rounded-lg p-4">
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Source Column (with CDS URLs)</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Destination Column (signed URLs)</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="100px">Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {Object.entries(columnMapping).map(([sourceColumn, destinationColumn]) => (
                <Table.Row key={sourceColumn}>
                  <Table.Cell>
                    {columns.length > 0 ? (
                      <Select.Root
                        value={sourceColumn}
                        onValueChange={newSourceColumn =>
                          handleSourceColumnChange(sourceColumn, newSourceColumn)
                        }
                      >
                        <Select.Trigger className="w-full" placeholder="Select source column..." />
                        <Select.Content>
                          {columns.map(column => (
                            <Select.Item key={column.id} value={column.id}>
                              {column.header || column.id}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    ) : (
                      <TextField.Root
                        value={sourceColumn}
                        onChange={e => handleSourceColumnChange(sourceColumn, e.target.value)}
                        placeholder="e.g., responses"
                        className="w-full"
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <TextField.Root
                      value={destinationColumn}
                      onChange={e => handleColumnMappingChange(sourceColumn, e.target.value)}
                      placeholder="e.g., responses_signed"
                      className="w-full"
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      variant="soft"
                      color="red"
                      size="1"
                      onClick={() => handleRemoveMapping(sourceColumn)}
                    >
                      <Icon icon={faTrash} label="Remove" />
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          <div className="mt-4">
            <Button variant="soft" color="blue" size="1" onClick={handleAddMapping}>
              <Icon icon={faPlus} label="Add" />
              Add Column Mapping
            </Button>
          </div>
        </div>
        <FormField.Description>
          Map source columns containing JSON with CDS URLs to destination column names for the
          signed URLs. The transformer will replace all private CDS URLs with signed URLs.
        </FormField.Description>
      </FormField.Root>
    </div>
  );
};

const S3UrlSigningParamsComponent: React.FC<{
  params: S3UrlSigningParams;
  onChange: (params: S3UrlSigningParams) => void;
  columns?: Column[];
}> = ({ params, onChange, columns = [] }) => {
  const columnMapping = params.columnMapping ?? DEFAULT_S3_URL_SIGNING_COLUMN_MAPPING;
  const ttlSeconds = params.ttlSeconds ?? 7 * 24 * 60 * 60; // Default: 7 days

  // Automatically set the first column when columns become available and we're using the default empty mapping
  useEffect(() => {
    if (
      columns.length > 0 &&
      (!params.columnMapping ||
        (Object.keys(params.columnMapping).length === 1 &&
          Object.keys(params.columnMapping)[0] === '' &&
          params.columnMapping[''] === ''))
    ) {
      const newMapping = { [columns[0].id]: '' };
      onChange({ ...params, columnMapping: newMapping });
    }
  }, [columns, params, onChange]);

  const handleColumnMappingChange = (sourceColumn: string, destinationColumn: string) => {
    const newMapping = { ...columnMapping, [sourceColumn]: destinationColumn };
    onChange({ ...params, columnMapping: newMapping });
  };

  const handleAddMapping = () => {
    // Generate a unique placeholder source column name to avoid conflicts
    let counter = 1;
    let sourceColumnName = 'new_source_column';
    while (columnMapping[sourceColumnName]) {
      sourceColumnName = `new_source_column_${counter}`;
      counter++;
    }

    const newMapping = { ...columnMapping, [sourceColumnName]: 'new_destination_column' };
    onChange({ ...params, columnMapping: newMapping });
  };

  const handleRemoveMapping = (sourceColumn: string) => {
    const newMapping = { ...columnMapping };
    delete newMapping[sourceColumn];
    onChange({ ...params, columnMapping: newMapping });
  };

  const handleSourceColumnChange = (oldSourceColumn: string, newSourceColumn: string) => {
    if (oldSourceColumn === newSourceColumn) return;

    const newMapping = { ...columnMapping };
    const destinationColumn = newMapping[oldSourceColumn];
    delete newMapping[oldSourceColumn];
    newMapping[newSourceColumn] = destinationColumn;
    onChange({ ...params, columnMapping: newMapping });
  };

  const handleTtlSecondsChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      onChange({ ...params, ttlSeconds: numValue });
    }
  };

  return (
    <div className="flex flex-col gap-4 max-w-4xl">
      <Callout.Root color="neutral" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          Configure column mappings for S3 URL Signing transformation. Specify which columns contain
          JSON with private S3 URLs and what to name the output columns with signed URLs.
        </Callout.Text>
      </Callout.Root>

      {columns.length === 0 && (
        <Callout.Root color="red" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Warning" />
          </Callout.Icon>
          <Callout.Text>
            No data columns available. You need to have data from a previous step to use this
            transformation.
          </Callout.Text>
        </Callout.Root>
      )}

      <FormField.Root className="mb-4">
        <FormField.Label>TTL for Signed URLs (seconds)</FormField.Label>
        <TextField.Root
          type="number"
          min="60"
          value={ttlSeconds.toString()}
          onChange={e => handleTtlSecondsChange(e.target.value)}
          placeholder="604800"
          className="max-w-xs"
        />
        <FormField.Description>
          Time-to-live for signed URLs in seconds. Default is 604800 seconds (7 days). Common
          values: 3600 (1 hour), 86400 (1 day), 604800 (7 days).
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>Column Mapping</FormField.Label>
        <div className="border rounded-lg p-4">
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Source Column (with S3 URLs)</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Destination Column (signed URLs)</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="100px">Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {Object.entries(columnMapping).map(([sourceColumn, destinationColumn]) => (
                <Table.Row key={sourceColumn}>
                  <Table.Cell>
                    {columns.length > 0 ? (
                      <Select.Root
                        value={sourceColumn}
                        onValueChange={newSourceColumn =>
                          handleSourceColumnChange(sourceColumn, newSourceColumn)
                        }
                      >
                        <Select.Trigger className="w-full" placeholder="Select source column..." />
                        <Select.Content>
                          {columns.map(column => (
                            <Select.Item key={column.id} value={column.id}>
                              {column.header || column.id}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    ) : (
                      <TextField.Root
                        value={sourceColumn}
                        onChange={e => handleSourceColumnChange(sourceColumn, e.target.value)}
                        placeholder="e.g., responses"
                        className="w-full"
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <TextField.Root
                      value={destinationColumn}
                      onChange={e => handleColumnMappingChange(sourceColumn, e.target.value)}
                      placeholder="e.g., responses_signed"
                      className="w-full"
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      variant="soft"
                      color="red"
                      size="1"
                      onClick={() => handleRemoveMapping(sourceColumn)}
                    >
                      <Icon icon={faTrash} label="Remove" />
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          <div className="mt-4">
            <Button variant="soft" color="blue" size="1" onClick={handleAddMapping}>
              <Icon icon={faPlus} label="Add" />
              Add Column Mapping
            </Button>
          </div>
        </div>
        <FormField.Description>
          Map source columns containing JSON with S3 URLs to destination column names for the signed
          URLs. The transformer will replace all private S3 URLs with signed URLs.
        </FormField.Description>
      </FormField.Root>
    </div>
  );
};

const HubstaffScreenshotParamsComponent: React.FC<{
  params: HubstaffScreenshotParams;
  onChange: (params: HubstaffScreenshotParams) => void;
}> = ({ params, onChange }) => {
  const projectId = params.project_id || '3686763';
  const hoursBack = params.hours_back ?? 24;
  const endTimestamp = params.end_timestamp || '';
  const [timestampError, setTimestampError] = useState<string>('');

  const handleProjectIdChange = (newProjectId: string) => {
    onChange({ ...params, project_id: newProjectId });
  };

  const handleHoursBackChange = (newHoursBack: string) => {
    const numValue = parseInt(newHoursBack, 10);
    if (!isNaN(numValue) && numValue > 0) {
      onChange({ ...params, hours_back: numValue });
    }
  };

  const handleEndTimestampChange = (newTimestamp: string) => {
    // Validate the timestamp format
    if (newTimestamp && newTimestamp.trim() !== '') {
      const date = new Date(newTimestamp);
      if (isNaN(date.getTime())) {
        setTimestampError('Invalid timestamp format');
      } else if (date.toISOString() !== newTimestamp) {
        setTimestampError('Must be in ISO 8601 format (e.g., 2025-01-15T10:30:00.000Z)');
      } else {
        setTimestampError('');
      }
    } else {
      setTimestampError('');
    }
    onChange({ ...params, end_timestamp: newTimestamp });
  };

  return (
    <div className="flex flex-col gap-4 max-w-4xl">
      <Callout.Root color="neutral" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          Fetches screenshots from Hubstaff using centralized authentication. This will fetch all
          screenshots from the specified project along with user information, merged by user ID.
        </Callout.Text>
      </Callout.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>Project ID</FormField.Label>
        <TextField.Root
          value={projectId}
          onChange={e => handleProjectIdChange(e.target.value)}
          placeholder="3686763"
          className="max-w-md"
        />
        <FormField.Description>
          Hubstaff Project ID to fetch screenshots from. Defaults to 3686763 (Mango project).
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>End Timestamp (Optional)</FormField.Label>
        <TextField.Root
          value={endTimestamp}
          onChange={e => handleEndTimestampChange(e.target.value)}
          placeholder="2025-01-15T10:30:00.000Z or leave empty for current time"
          className="max-w-md font-mono"
        />
        {timestampError && (
          <Text size="2" color="red" className="mt-1">
            {timestampError}
          </Text>
        )}
        <FormField.Description>
          ISO 8601 timestamp to use as the end time (stop time) for the query. Leave empty to use
          the current time. Format: YYYY-MM-DDTHH:mm:ss.sssZ
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>Hours Back</FormField.Label>
        <TextField.Root
          type="number"
          min="1"
          value={hoursBack.toString()}
          onChange={e => handleHoursBackChange(e.target.value)}
          placeholder="24"
          className="max-w-xs"
        />
        <FormField.Description>
          Number of hours to look back from the end timestamp. Defaults to 24 hours. Common values:
          24 (1 day), 72 (3 days), 168 (7 days).
        </FormField.Description>
      </FormField.Root>

      <Callout.Root color="blue" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          This transformer will fetch all screenshots and user data in a single request, then merge
          them by user ID to provide a comprehensive view of screenshots with associated user
          details.
        </Callout.Text>
      </Callout.Root>
    </div>
  );
};

const MultiMangoQATableParamsComponent: React.FC<{
  params: MultiMangoQATableParams;
  onChange: (params: MultiMangoQATableParams) => void;
}> = ({ params, onChange }) => {
  const defaultParams = getDefaultScriptParams(
    CompassTransformScript.MULTIMANGO_QA_TABLE,
  ) as MultiMangoQATableParams;

  const email = params.email || '';
  const password = params.password || '';
  const dateFilter = params.dateFilter || defaultParams.dateFilter || 'all';
  const expandTaskColumns = params.expandTaskColumns ?? defaultParams.expandTaskColumns ?? true;

  const handleEmailChange = (newEmail: string) => {
    onChange({ ...params, email: newEmail });
  };

  const handlePasswordChange = (newPassword: string) => {
    onChange({ ...params, password: newPassword });
  };

  const handleDateFilterChange = (newDateFilter: string) => {
    onChange({ ...params, dateFilter: newDateFilter as 'all' | '24h' });
  };

  const handleExpandTaskColumnsChange = (checked: boolean) => {
    onChange({ ...params, expandTaskColumns: checked });
  };

  return (
    <div className="flex flex-col gap-4 max-w-4xl">
      <Callout.Root color="neutral" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          Configure authentication credentials and filters for MultiMango.com. This will fetch
          quality assurance data from the quality-assurance page.
        </Callout.Text>
      </Callout.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>
          Email <span className="text-red-500">*</span>
        </FormField.Label>
        <TextField.Root
          value={email}
          onChange={e => handleEmailChange(e.target.value)}
          placeholder="Enter email address"
          className="max-w-md"
        />
        <FormField.Description>Email address for MultiMango authentication.</FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>
          Password <span className="text-red-500">*</span>
        </FormField.Label>
        <TextField.Root
          type="password"
          value={password}
          onChange={e => handlePasswordChange(e.target.value)}
          placeholder="Enter password"
          className="max-w-md"
          autoComplete="off"
        />
        <FormField.Description>
          Password for MultiMango authentication. This is required to fetch the data.
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>Date Filter</FormField.Label>
        <Select.Root value={dateFilter} onValueChange={handleDateFilterChange}>
          <Select.Trigger className="max-w-md" />
          <Select.Content>
            <Select.Item value="all">All Time</Select.Item>
            <Select.Item value="24h">Last 24 Hours</Select.Item>
          </Select.Content>
        </Select.Root>
        <FormField.Description>
          &quot;All Time&quot; fetches all users. &quot;Last 24 Hours&quot; fetches only users
          active in the last day.
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <Flex align="center" gap="2">
          <input
            type="checkbox"
            checked={expandTaskColumns}
            onChange={e => handleExpandTaskColumnsChange(e.target.checked)}
            className="cursor-pointer"
          />
          <FormField.Label
            className="cursor-pointer"
            onClick={() => handleExpandTaskColumnsChange(!expandTaskColumns)}
          >
            Expand Task Columns
          </FormField.Label>
        </Flex>
        <FormField.Description>
          When checked: Creates separate columns for each task (better for viewing in Compass). When
          unchecked: Stores all task data in one JSON column (better for Snowflake APPEND mode with
          stable schema).
        </FormField.Description>
      </FormField.Root>

      {(!email || !password) && (
        <Callout.Root color="red" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Warning" />
          </Callout.Icon>
          <Callout.Text>Email and password are required to run this transformation.</Callout.Text>
        </Callout.Root>
      )}
    </div>
  );
};

const SetCBRoleParamsComponent: React.FC<{
  params: SetCBRoleParams;
  onChange: (params: SetCBRoleParams) => void;
  columns?: Column[];
}> = ({ params, onChange, columns = [] }) => {
  const emailColumn = params.emailColumn ?? '';
  const role = params.role ?? 'Unknown';
  const outputColumn = params.outputColumn ?? 'cb_role_status';

  // Auto-select first column if available
  useEffect(() => {
    if (columns.length > 0 && !params.emailColumn) {
      onChange({ ...params, emailColumn: columns[0].id });
    }
  }, [columns, params.emailColumn, onChange, params]);

  const handleEmailColumnChange = (value: string) => {
    onChange({ ...params, emailColumn: value });
  };

  const handleRoleChange = (value: string) => {
    onChange({ ...params, role: value as 'Unapproved' | 'Unknown' | 'Approved' });
  };

  const handleOutputColumnChange = (value: string) => {
    onChange({ ...params, outputColumn: value });
  };

  // Validation
  const isEmailColumnEmpty = emailColumn.length === 0;

  return (
    <div className="flex flex-col gap-4 max-w-4xl">
      <Callout.Root color="neutral" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          Configure CB (Community Builder) role assignment. Select the column containing contributor
          email addresses and specify the role to assign.
        </Callout.Text>
      </Callout.Root>

      {columns.length === 0 && (
        <Callout.Root color="red" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Warning" />
          </Callout.Icon>
          <Callout.Text>
            No data columns available. You need to have data from a previous step to use this
            transformation.
          </Callout.Text>
        </Callout.Root>
      )}

      {isEmailColumnEmpty && (
        <Callout.Root color="orange" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Warning" />
          </Callout.Icon>
          <Callout.Text>Email column is required to run this transformation.</Callout.Text>
        </Callout.Root>
      )}

      <FormField.Root className="mb-4">
        <FormField.Label>Email Column (Mango email)</FormField.Label>
        {columns.length > 0 ? (
          <Select.Root value={emailColumn} onValueChange={handleEmailColumnChange}>
            <Select.Trigger className="w-full max-w-xs" placeholder="Select email column..." />
            <Select.Content>
              {columns.map(column => (
                <Select.Item key={column.id} value={column.id}>
                  {column.header || column.id}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        ) : (
          <TextField.Root
            value={emailColumn}
            onChange={e => handleEmailColumnChange(e.target.value)}
            placeholder="e.g., contributor_email"
            className="max-w-xs"
          />
        )}
        <FormField.Description>
          Column containing the contributor email addresses.
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>CB Role</FormField.Label>
        <Select.Root value={role} onValueChange={handleRoleChange}>
          <Select.Trigger className="w-full max-w-xs" placeholder="Select role..." />
          <Select.Content>
            <Select.Item value="Unapproved Annotator">Unapproved Annotator</Select.Item>
            <Select.Item value="Unknown">Unknown</Select.Item>
            <Select.Item value="Approved Annotator">Approved Annotator</Select.Item>
          </Select.Content>
        </Select.Root>
        <FormField.Description>
          The CB (Community Builder) role to assign to the contributors.
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>Output Column Name</FormField.Label>
        <TextField.Root
          value={outputColumn}
          onChange={e => handleOutputColumnChange(e.target.value)}
          placeholder="e.g., cb_role_status"
          className="max-w-xs"
        />
        <FormField.Description>
          Name of the column that will contain the role assignment status (e.g., SUCCESS, FAILED).
        </FormField.Description>
      </FormField.Root>
    </div>
  );
};

const MultimangoPasswordResetParamsComponent: React.FC<{
  params: MultimangoPasswordResetParams;
  onChange: (params: MultimangoPasswordResetParams) => void;
  columns?: Column[];
}> = ({ params, onChange, columns = [] }) => {
  const emailColumn = params.emailColumn ?? '';
  const newPassword = params.newPassword ?? '';
  const outputColumn = params.outputColumn ?? 'password_reset_status';
  const [showPassword, setShowPassword] = useState(false);

  // Auto-select first column if available
  useEffect(() => {
    if (columns.length > 0 && !params.emailColumn) {
      onChange({ ...params, emailColumn: columns[0].id });
    }
  }, [columns, params.emailColumn, onChange, params]);

  const handleEmailColumnChange = (value: string) => {
    onChange({ ...params, emailColumn: value });
  };

  const handleNewPasswordChange = (value: string) => {
    onChange({ ...params, newPassword: value });
  };

  const handleOutputColumnChange = (value: string) => {
    onChange({ ...params, outputColumn: value });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Validation
  const isPasswordTooShort = newPassword.length > 0 && newPassword.length < 8;
  const isPasswordEmpty = newPassword.length === 0;

  return (
    <div className="flex flex-col gap-4 max-w-4xl">
      <Callout.Root color="neutral" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          Configure Multimango user password reset automation. Select the column containing
          contributor email addresses and specify the new password to set for all users.
        </Callout.Text>
      </Callout.Root>

      {columns.length === 0 && (
        <Callout.Root color="red" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Warning" />
          </Callout.Icon>
          <Callout.Text>
            No data columns available. You need to have data from a previous step to use this
            transformation.
          </Callout.Text>
        </Callout.Root>
      )}

      {isPasswordTooShort && (
        <Callout.Root color="red" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Error" />
          </Callout.Icon>
          <Callout.Text>
            Password must be at least 8 characters long. Current length: {newPassword.length}{' '}
            characters.
          </Callout.Text>
        </Callout.Root>
      )}

      {isPasswordEmpty && (
        <Callout.Root color="orange" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Warning" />
          </Callout.Icon>
          <Callout.Text>New password is required to run this transformation.</Callout.Text>
        </Callout.Root>
      )}

      <FormField.Root className="mb-4">
        <FormField.Label>Email Column (Mango email)</FormField.Label>
        {columns.length > 0 ? (
          <Select.Root value={emailColumn} onValueChange={handleEmailColumnChange}>
            <Select.Trigger className="w-full max-w-xs" placeholder="Select email column..." />
            <Select.Content>
              {columns.map(column => (
                <Select.Item key={column.id} value={column.id}>
                  {column.header || column.id}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        ) : (
          <TextField.Root
            value={emailColumn}
            onChange={e => handleEmailColumnChange(e.target.value)}
            placeholder="e.g., contributor_email"
            className="max-w-xs"
          />
        )}
        <FormField.Description>
          Column containing the contributor email addresses for Multimango accounts.
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>New Password</FormField.Label>
        <div className="relative max-w-xs">
          <TextField.Root
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={e => handleNewPasswordChange(e.target.value)}
            placeholder="Enter new password"
            className="pr-10"
          />
          <Button
            type="button"
            variant="ghost"
            size="1"
            className="absolute right-1 top-1/2 -translate-y-1/2"
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon icon={showPassword ? faEyeSlash : faEye} label={showPassword ? 'Hide' : 'Show'} />
          </Button>
        </div>
        <FormField.Description>
          The new password that will be set for all Multimango users in this dataset.
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>Output Column Name</FormField.Label>
        <TextField.Root
          value={outputColumn}
          onChange={e => handleOutputColumnChange(e.target.value)}
          placeholder="e.g., password_reset_status"
          className="max-w-xs"
        />
        <FormField.Description>
          Name of the column that will contain the password reset status (e.g., SUCCESS, FAILED).
        </FormField.Description>
      </FormField.Root>
    </div>
  );
};

const SyncMultimangoUsersParamsComponent: React.FC<{
  params: SyncMultimangoUsersParams;
  onChange: (params: SyncMultimangoUsersParams) => void;
  columns?: Column[];
}> = ({ params, onChange, columns = [] }) => {
  const userIdColumn = params.userIdColumn ?? '';
  const userPersonalEmailColumn = params.userPersonalEmailColumn ?? '';
  const userWfeEmailColumn = params.userWfeEmailColumn ?? '';
  const wfePasswordColumn = params.wfePasswordColumn ?? '';
  const outputColumn = params.outputColumn ?? 'sync_status';

  // Auto-fill default column values if they exist in the available columns
  useEffect(() => {
    if (columns.length === 0) return;

    const updates: Partial<SyncMultimangoUsersParams> = {};

    // Auto-select 'worker_id' for User ID Column if available and not already set
    if (!params.userIdColumn) {
      const workerIdColumn = columns.find(col => col.id === 'worker_id');
      if (workerIdColumn) {
        updates.userIdColumn = workerIdColumn.id;
      }
    }

    // Auto-select 'outlier_email' for User Personal Email Column if available and not already set
    if (!params.userPersonalEmailColumn) {
      const outlierEmailColumn = columns.find(col => col.id === 'outlier_email');
      if (outlierEmailColumn) {
        updates.userPersonalEmailColumn = outlierEmailColumn.id;
      }
    }

    // Auto-select 'email' for User WFE Email Column if available and not already set
    if (!params.userWfeEmailColumn) {
      const emailColumn = columns.find(col => col.id === 'email');
      if (emailColumn) {
        updates.userWfeEmailColumn = emailColumn.id;
      }
    }

    // Auto-select 'password' for WFE Password Column if available and not already set
    if (!params.wfePasswordColumn) {
      const passwordColumn = columns.find(col => col.id === 'password');
      if (passwordColumn) {
        updates.wfePasswordColumn = passwordColumn.id;
      }
    }

    // Only update if there are changes to apply
    if (Object.keys(updates).length > 0) {
      onChange({ ...params, ...updates });
    }
  }, [columns, params, onChange]);

  const handleUserIdColumnChange = (value: string) => {
    onChange({ ...params, userIdColumn: value });
  };

  const handleUserPersonalEmailColumnChange = (value: string) => {
    onChange({ ...params, userPersonalEmailColumn: value });
  };

  const handleUserWfeEmailColumnChange = (value: string) => {
    onChange({ ...params, userWfeEmailColumn: value });
  };

  const handleWfePasswordColumnChange = (value: string) => {
    onChange({ ...params, wfePasswordColumn: value });
  };

  const handleOutputColumnChange = (value: string) => {
    onChange({ ...params, outputColumn: value });
  };

  // Validation
  const isUserIdColumnEmpty = userIdColumn.length === 0;
  const isUserPersonalEmailColumnEmpty = userPersonalEmailColumn.length === 0;
  const isUserWfeEmailColumnEmpty = userWfeEmailColumn.length === 0;
  const isWfePasswordColumnEmpty = wfePasswordColumn.length === 0;
  const hasAnyRequiredFieldEmpty =
    isUserIdColumnEmpty ||
    isUserPersonalEmailColumnEmpty ||
    isUserWfeEmailColumnEmpty ||
    isWfePasswordColumnEmpty;

  return (
    <div className="flex flex-col gap-4 max-w-4xl">
      <Callout.Root color="neutral" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          Configure field mapping to sync Multimango user credentials to MongoDB. Map columns from
          the previous step to the required MongoDB fields. Users will be created if they don't
          exist or updated if they already exist in the database.
        </Callout.Text>
      </Callout.Root>

      {columns.length === 0 && (
        <Callout.Root color="red" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Warning" />
          </Callout.Icon>
          <Callout.Text>
            No data columns available. You need to have data from a previous step to use this
            transformation.
          </Callout.Text>
        </Callout.Root>
      )}

      {hasAnyRequiredFieldEmpty && (
        <Callout.Root color="orange" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Warning" />
          </Callout.Icon>
          <Callout.Text>
            All field mappings are required to run this transformation. Please map all required
            fields.
          </Callout.Text>
        </Callout.Root>
      )}

      <FormField.Root className="mb-4">
        <FormField.Label>
          User ID Column <span className="text-red-500">*</span>
        </FormField.Label>
        {columns.length > 0 ? (
          <Select.Root value={userIdColumn} onValueChange={handleUserIdColumnChange}>
            <Select.Trigger className="w-full max-w-xs" placeholder="Select user ID column..." />
            <Select.Content>
              {columns.map(column => (
                <Select.Item key={column.id} value={column.id}>
                  {column.header || column.id}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        ) : (
          <TextField.Root
            value={userIdColumn}
            onChange={e => handleUserIdColumnChange(e.target.value)}
            placeholder="e.g., user_id"
            className="max-w-xs"
          />
        )}
        <FormField.Description>
          Column containing the MongoDB userId (ObjectId or string that can be converted to
          ObjectId).
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>
          User Outlier Email Column <span className="text-red-500">*</span>
        </FormField.Label>
        {columns.length > 0 ? (
          <Select.Root
            value={userPersonalEmailColumn}
            onValueChange={handleUserPersonalEmailColumnChange}
          >
            <Select.Trigger
              className="w-full max-w-xs"
              placeholder="Select personal email column..."
            />
            <Select.Content>
              {columns.map(column => (
                <Select.Item key={column.id} value={column.id}>
                  {column.header || column.id}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        ) : (
          <TextField.Root
            value={userPersonalEmailColumn}
            onChange={e => handleUserPersonalEmailColumnChange(e.target.value)}
            placeholder="e.g., personal_email"
            className="max-w-xs"
          />
        )}
        <FormField.Description>
          Column containing the user&apos;s personal email address.
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>
          User WFE Email Column <span className="text-red-500">*</span>
        </FormField.Label>
        {columns.length > 0 ? (
          <Select.Root value={userWfeEmailColumn} onValueChange={handleUserWfeEmailColumnChange}>
            <Select.Trigger className="w-full max-w-xs" placeholder="Select WFE email column..." />
            <Select.Content>
              {columns.map(column => (
                <Select.Item key={column.id} value={column.id}>
                  {column.header || column.id}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        ) : (
          <TextField.Root
            value={userWfeEmailColumn}
            onChange={e => handleUserWfeEmailColumnChange(e.target.value)}
            placeholder="e.g., wfe_email"
            className="max-w-xs"
          />
        )}
        <FormField.Description>
          Column containing the user&apos;s WFE (Work From Everywhere) email address.
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>
          WFE Password Column <span className="text-red-500">*</span>
        </FormField.Label>
        {columns.length > 0 ? (
          <Select.Root value={wfePasswordColumn} onValueChange={handleWfePasswordColumnChange}>
            <Select.Trigger className="w-full max-w-xs" placeholder="Select password column..." />
            <Select.Content>
              {columns.map(column => (
                <Select.Item key={column.id} value={column.id}>
                  {column.header || column.id}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        ) : (
          <TextField.Root
            value={wfePasswordColumn}
            onChange={e => handleWfePasswordColumnChange(e.target.value)}
            placeholder="e.g., password"
            className="max-w-xs"
          />
        )}
        <FormField.Description>
          Column containing the WFE password (will be encrypted before storage).
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>Output Column Name</FormField.Label>
        <TextField.Root
          value={outputColumn}
          onChange={e => handleOutputColumnChange(e.target.value)}
          placeholder="e.g., sync_status"
          className="max-w-xs"
        />
        <FormField.Description>
          Name of the column that will contain the sync status (e.g., CREATED, UPDATED, FAILED).
        </FormField.Description>
      </FormField.Root>
    </div>
  );
};

const WfeDecryptPasswordsParamsComponent: React.FC<{
  params: WfeDecryptPasswordsParams;
  onChange: (params: WfeDecryptPasswordsParams) => void;
  columns?: Column[];
}> = ({ params, onChange, columns = [] }) => {
  const passwordColumn = params.passwordColumn ?? '';
  const outputColumn = params.outputColumn ?? 'password_decrypted';

  // Auto-select first column if available
  useEffect(() => {
    if (columns.length > 0 && !params.passwordColumn) {
      onChange({ ...params, passwordColumn: columns[0].id });
    }
  }, [columns, params.passwordColumn, onChange, params]);

  const handlePasswordColumnChange = (value: string) => {
    onChange({ ...params, passwordColumn: value });
  };

  const handleOutputColumnChange = (value: string) => {
    onChange({ ...params, outputColumn: value });
  };

  // Validation
  const isPasswordColumnEmpty = passwordColumn.length === 0;

  return (
    <div className="flex flex-col gap-4 max-w-4xl">
      <Callout.Root color="neutral" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          Configure WFE password decryption. Select the column containing encrypted passwords (e.g.,
          srtPassword, googlePassword, wfePassword) and specify the output column name for the
          decrypted values.
        </Callout.Text>
      </Callout.Root>

      {columns.length === 0 && (
        <Callout.Root color="red" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Warning" />
          </Callout.Icon>
          <Callout.Text>
            No data columns available. You need to have data from a previous step to use this
            transformation.
          </Callout.Text>
        </Callout.Root>
      )}

      {isPasswordColumnEmpty && (
        <Callout.Root color="orange" className="mb-4">
          <Callout.Icon>
            <Icon icon={faExclamationTriangle} label="Warning" />
          </Callout.Icon>
          <Callout.Text>Password column is required to run this transformation.</Callout.Text>
        </Callout.Root>
      )}

      <FormField.Root className="mb-4">
        <FormField.Label>
          Password Column <span className="text-red-500">*</span>
        </FormField.Label>
        {columns.length > 0 ? (
          <Select.Root value={passwordColumn} onValueChange={handlePasswordColumnChange}>
            <Select.Trigger className="w-full max-w-xs" placeholder="Select password column..." />
            <Select.Content>
              {columns.map(column => (
                <Select.Item key={column.id} value={column.id}>
                  {column.header || column.id}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        ) : (
          <TextField.Root
            value={passwordColumn}
            onChange={e => handlePasswordColumnChange(e.target.value)}
            placeholder="e.g., srtPassword"
            className="max-w-xs"
          />
        )}
        <FormField.Description>
          Column containing the encrypted password (e.g., srtPassword, googlePassword, wfePassword).
        </FormField.Description>
      </FormField.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>Output Column Name</FormField.Label>
        <TextField.Root
          value={outputColumn}
          onChange={e => handleOutputColumnChange(e.target.value)}
          placeholder="e.g., password_decrypted"
          className="max-w-xs"
        />
        <FormField.Description>
          Name of the column that will contain the decrypted password. Defaults to
          &quot;password_decrypted&quot;.
        </FormField.Description>
      </FormField.Root>

      <Callout.Root color="blue" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          This transformer uses the wfe.secret config value for decryption. It handles passwords
          encrypted with Cryptr, including those with the ENC:: prefix.
        </Callout.Text>
      </Callout.Root>
    </div>
  );
};

const AgentExecutionParamsComponent: React.FC<{
  params: AgentExecutionParams;
  onChange: (params: AgentExecutionParams) => void;
}> = ({ params, onChange }) => {
  const defaultParams = getDefaultScriptParams(
    CompassTransformScript.AGENT_EXECUTION,
  ) as AgentExecutionParams;

  const model = params.model || defaultParams.model;

  const handleModelChange = (newModel: string) => {
    onChange({ ...params, model: newModel });
  };

  return (
    <div className="flex flex-col gap-4 max-w-4xl">
      <Callout.Root color="neutral" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          Configure the model to use for agent execution. The model will be used to run the agent
          tasks with the specified MCP tools.
        </Callout.Text>
      </Callout.Root>

      <FormField.Root className="mb-4">
        <FormField.Label>Model</FormField.Label>
        <TextField.Root
          value={model}
          onChange={e => handleModelChange(e.target.value)}
          placeholder="e.g., openai/gpt-5, gemini/gemini-2.5-pro"
          className="max-w-md font-mono"
        />
        <FormField.Description>
          The model to use for agent execution. Default is &quot;openai/gpt-4o&quot;. Supported
          formats: openai/model-name, anthropic/model-name, etc.
        </FormField.Description>
      </FormField.Root>

      <Callout.Root color="blue" className="mb-4">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          This script requires input columns: TASK, PROMPT, IMAGE, and ENABLED_TOOLS. Optional
          columns: MCP_SERVER_REGISTRY.
        </Callout.Text>
      </Callout.Root>
    </div>
  );
};

const ScriptParamsComponent: React.FC<ScriptParamsComponentProps> = ({
  script,
  params,
  onChange,
  columns,
}) => {
  // Initialize params with defaults if needed
  useEffect(() => {
    if (script) {
      const defaultParams = getDefaultScriptParams(script);
      if (defaultParams && (Object.keys(params).length === 0 || params === undefined)) {
        onChange(defaultParams);
      }
    }
  }, [script, params, onChange]);

  if (!script) {
    return null;
  }

  // Render different parameter editors based on script type
  switch (script) {
    case CompassTransformScript.OTS_UI_PROMPT_GENERATION:
      return (
        <OTSUIPromptGenerationParamsComponent
          params={params as OTSUIPromptGenerationParams}
          onChange={onChange as (params: OTSUIPromptGenerationParams) => void}
        />
      );
    case CompassTransformScript.CDS_DOWNLOAD_RESPONSE:
      return (
        <CDSDownloadResponseParamsComponent
          params={params as CDSDownloadResponseParams}
          onChange={onChange as (params: CDSDownloadResponseParams) => void}
        />
      );
    case CompassTransformScript.CDS_CONTENT_INSERTION:
      return (
        <CDSContentInsertionParamsComponent
          params={params as CDSContentInsertionParams}
          onChange={onChange as (params: CDSContentInsertionParams) => void}
          columns={columns}
        />
      );
    case CompassTransformScript.CDS_URL_SIGNING:
      return (
        <CDSUrlSigningParamsComponent
          params={params as CDSUrlSigningParams}
          onChange={onChange as (params: CDSUrlSigningParams) => void}
          columns={columns}
        />
      );
    case CompassTransformScript.S3_URL_SIGNING:
      return (
        <S3UrlSigningParamsComponent
          params={params as S3UrlSigningParams}
          onChange={onChange as (params: S3UrlSigningParams) => void}
          columns={columns}
        />
      );
    case CompassTransformScript.HUBSTAFF_SCREENSHOT:
      return (
        <HubstaffScreenshotParamsComponent
          params={params as HubstaffScreenshotParams}
          onChange={onChange as (params: HubstaffScreenshotParams) => void}
        />
      );
    case CompassTransformScript.MULTIMANGO_QA_TABLE:
      return (
        <MultiMangoQATableParamsComponent
          params={params as MultiMangoQATableParams}
          onChange={onChange as (params: MultiMangoQATableParams) => void}
        />
      );
    case CompassTransformScript.MULTIMANGO_USER_PASSWORD_RESET:
      return (
        <MultimangoPasswordResetParamsComponent
          params={params as MultimangoPasswordResetParams}
          onChange={onChange as (params: MultimangoPasswordResetParams) => void}
          columns={columns}
        />
      );
    case CompassTransformScript.SET_CB_ROLE:
      return (
        <SetCBRoleParamsComponent
          params={params as SetCBRoleParams}
          onChange={onChange as (params: SetCBRoleParams) => void}
          columns={columns}
        />
      );
    case CompassTransformScript.SYNC_MULTIMANGO_USERS:
      return (
        <SyncMultimangoUsersParamsComponent
          params={params as SyncMultimangoUsersParams}
          onChange={onChange as (params: SyncMultimangoUsersParams) => void}
          columns={columns}
        />
      );
    case CompassTransformScript.WFE_DECRYPT_PASSWORDS:
      return (
        <WfeDecryptPasswordsParamsComponent
          params={params as WfeDecryptPasswordsParams}
          onChange={onChange as (params: WfeDecryptPasswordsParams) => void}
          columns={columns}
        />
      );
    case CompassTransformScript.AGENT_EXECUTION:
      return (
        <AgentExecutionParamsComponent
          params={params as AgentExecutionParams}
          onChange={onChange as (params: AgentExecutionParams) => void}
        />
      );
    default:
      return (
        <div className="p-4 text-center">
          <Text>No configurable parameters available for this script.</Text>
        </div>
      );
  }
};

export default ScriptParamsComponent;
